import React from 'react';
import {
  Grid,
  Box,
  Typography,
  Avatar,
  Skeleton,
  Chip,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider
} from '@mui/material';
import {
  YouTube,
  TrendingUp,
  TrendingDown,
  Visibility,
  VideoLibrary,
  People,
  ThumbUp,
  Comment,
  Schedule,
  CheckCircle
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { youtubeAPI } from '../../services/api';
import KPICard from '../charts/KPICard';
import SectionTitle from '../crt/SectionTitle';
import Panel from '../crt/Panel';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { TextField, MenuItem, Box as MBox } from '@mui/material';

const ChannelOverview: React.FC = () => {
  const [days, setDays] = React.useState(30);
  const [topMetric, setTopMetric] = React.useState<'views'|'likes'|'comments'>('views');
  const [topLimit, setTopLimit] = React.useState(5);

  // Fetch channel summary
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['youtube-summary'],
    queryFn: youtubeAPI.getSummary,
    staleTime: 60000
  });

  // Fetch channel metrics over time
  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: ['channel-metrics', days],
    queryFn: () => youtubeAPI.getChannelMetrics(days),
    staleTime: 60000
  });

  // Fetch top videos
  const { data: topVideos, isLoading: videosLoading } = useQuery({
    queryKey: ['top-videos', topMetric, topLimit, days],
    queryFn: () => youtubeAPI.getTopVideos(topMetric, topLimit),
    staleTime: 60000
  });

  // Fetch analytics overview
  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['analytics-overview', days],
    queryFn: () => youtubeAPI.getAnalyticsOverview(days),
    staleTime: 60000
  });

  // Fetch comprehensive dashboard data for accurate 30-day metrics
  const { data: comprehensive, isLoading: comprehensiveLoading } = useQuery({
    queryKey: ['comprehensive-dashboard', days],
    queryFn: () => youtubeAPI.getComprehensiveDashboard(days),
    staleTime: 60000
  });

  const isLoading = summaryLoading || metricsLoading || videosLoading || analyticsLoading || comprehensiveLoading;

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatPercentage = (num: number) => {
    return `${num > 0 ? '+' : ''}${num.toFixed(1)}%`;
  };

  return (
    <Box>
      <SectionTitle>CHANNEL OVERVIEW</SectionTitle>
      {/* Channel Header */}
      {summary && (
        <Panel label={`CHANNEL :: ${(summary.channel.title || '').toUpperCase()}`} sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" gap={3}>
            <Avatar
              src={summary.channel.thumbnail_url}
              sx={{ width: 100, height: 100 }}
            >
              <YouTube fontSize="large" />
            </Avatar>
            <Box flex={1}>
              <Typography variant="h4" fontWeight={600} gutterBottom>
                {summary.channel.title}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                {summary.channel.description?.substring(0, 200)}...
              </Typography>
              <Box display="flex" gap={2}>
                <Chip
                  icon={<People />}
                  label={`${formatNumber(summary.channel.subscriber_count)} subscribers`}
                  color="primary"
                />
                <Chip
                  icon={<VideoLibrary />}
                  label={`${summary.channel.video_count} videos`}
                />
                <Chip
                  icon={<Visibility />}
                  label={`${formatNumber(summary.channel.view_count)} total views`}
                />
              </Box>
            </Box>
          </Box>
        </Panel>
      )}

      {/* Filters */}
      <Panel label="FILTERS" sx={{ mb: 2, p: 2 }}>
        <MBox display="flex" gap={2}>
          <TextField size="small" select label="Window" value={days} onChange={e => setDays(parseInt(e.target.value, 10))}>
            {[7, 14, 30, 60, 90].map(v => <MenuItem key={v} value={v}>{v} days</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Top Metric" value={topMetric} onChange={e => setTopMetric(e.target.value as any)}>
            <MenuItem value="views">Views</MenuItem>
            <MenuItem value="likes">Likes</MenuItem>
            <MenuItem value="comments">Comments</MenuItem>
          </TextField>
          <TextField size="small" select label="Top Limit" value={topLimit} onChange={e => setTopLimit(parseInt(e.target.value, 10))}>
            {[5,10,20].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </TextField>
        </MBox>
      </Panel>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={160} />
          ) : (
            <KPICard
              title="Net Subscribers (30d)"
              value={comprehensive?.metrics?.netSubscribers || summary?.growth?.subscriber_growth || 0}
              change={parseFloat(comprehensive?.growth?.subscribersChange) || 0}
              format="number"
              icon={<People />}
              color="#FF0000"
              subtitle={`${parseFloat(comprehensive?.growth?.subscribersChange) > 0 ? '+' : ''}${parseFloat(comprehensive?.growth?.subscribersChange)?.toFixed(1) || 0}% vs previous period`}
            />
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={160} />
          ) : (
            <KPICard
              title="Total Views (30d)"
              value={comprehensive?.metrics?.views || summary?.growth?.view_growth || 0}
              change={parseFloat(comprehensive?.growth?.viewsChange) || 0}
              format="number"
              icon={<Visibility />}
              color="#1976d2"
              subtitle={`${parseFloat(comprehensive?.growth?.viewsChange) > 0 ? '+' : ''}${parseFloat(comprehensive?.growth?.viewsChange)?.toFixed(1) || 0}% vs previous period`}
            />
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={160} />
          ) : (
            <KPICard
              title="Engagement Rate (30d)"
              value={comprehensive?.metrics ? ((comprehensive.metrics.likes + comprehensive.metrics.comments) / comprehensive.metrics.views) * 100 : 0}
              change={parseFloat(comprehensive?.growth?.engagementChange) || 0}
              format="percentage"
              icon={<ThumbUp />}
              color="#4caf50"
              subtitle={`${parseFloat(comprehensive?.growth?.engagementChange)?.toFixed(1) || 0}% vs previous period`}
            />
          )}
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={160} />
          ) : (
            <KPICard
              title="Watch Time (30d)"
              value={comprehensive?.metrics?.watchTimeHours || 0}
              change={parseFloat(comprehensive?.growth?.watchTimeChange) || 0}
              format="number"
              icon={<TrendingUp />}
              color="#ff9800"
              subtitle={`${parseFloat(comprehensive?.growth?.watchTimeChange) > 0 ? '+' : ''}${parseFloat(comprehensive?.growth?.watchTimeChange)?.toFixed(1) || 0}% vs previous period`}
            />
          )}
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Subscriber Growth Chart */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="SUBSCRIBER GROWTH" sx={{ height: 400 }}>
            {isLoading ? (
              <Skeleton variant="rectangular" height={320} />
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={metrics?.metrics || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis tickFormatter={formatNumber} />
                  <Tooltip 
                    formatter={(value: number) => formatNumber(value)}
                    labelFormatter={(date) => new Date(date).toLocaleDateString()}
                  />
                  <Area
                    type="monotone"
                    dataKey="subscribers"
                    stroke="#00ff41"
                    fill="#00ff41"
                    fillOpacity={0.15}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </Grid>

        {/* Views Growth Chart */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="VIEWS GROWTH" sx={{ height: 400 }}>
            {isLoading ? (
              <Skeleton variant="rectangular" height={320} />
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={metrics?.metrics || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis tickFormatter={formatNumber} />
                  <Tooltip 
                    formatter={(value: number) => formatNumber(value)}
                    labelFormatter={(date) => new Date(date).toLocaleDateString()}
                  />
                  <Area
                    type="monotone"
                    dataKey="views"
                    stroke="#00ffff"
                    fill="#00ffff"
                    fillOpacity={0.15}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </Grid>
      </Grid>

      {/* Bottom Row */}
      <Grid container spacing={3}>
        {/* Top Performing Videos */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="TOP PERFORMING VIDEOS">
            {isLoading ? (
              <>
                <Skeleton height={60} sx={{ mb: 1 }} />
                <Skeleton height={60} sx={{ mb: 1 }} />
                <Skeleton height={60} />
              </>
            ) : (
              <List>
                {topVideos?.top_videos.map((video: any, index: number) => (
                  <React.Fragment key={video.video_id}>
                    <ListItem alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar
                          variant="rounded"
                          src={video.thumbnail_url}
                          sx={{ width: 80, height: 45 }}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div' }}
                        primary={
                          <Typography variant="subtitle2" noWrap>
                            {video.title}
                          </Typography>
                        }
                        secondary={
                          <Box display="flex" gap={2} mt={0.5}>
                            <Chip
                              size="small"
                              icon={<Visibility />}
                              label={formatNumber(video.view_count)}
                            />
                            <Chip
                              size="small"
                              icon={<ThumbUp />}
                              label={formatNumber(video.like_count)}
                            />
                            <Chip
                              size="small"
                              icon={<Comment />}
                              label={formatNumber(video.comment_count)}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < topVideos.top_videos.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Panel>
        </Grid>

        {/* Quick Stats */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="30-DAY PERFORMANCE SUMMARY">
            {isLoading ? (
              <>
                <Skeleton height={80} sx={{ mb: 2 }} />
                <Skeleton height={80} sx={{ mb: 2 }} />
                <Skeleton height={80} />
              </>
            ) : (
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom variant="caption">
                        New Subscribers (30d)
                      </Typography>
                      <Typography variant="h5" fontWeight={600}>
                        {formatNumber(comprehensive?.metrics?.netSubscribers || analytics?.channel?.subscriber_growth || 0)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={6}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom variant="caption">
                        Total Views (30d)
                      </Typography>
                      <Typography variant="h5" fontWeight={600}>
                        {formatNumber(comprehensive?.metrics?.views || analytics?.channel?.view_growth || 0)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={6}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom variant="caption">
                        Videos Published (30d)
                      </Typography>
                      <Typography variant="h5" fontWeight={600}>
                        {comprehensive?.topContent?.length || analytics?.videos?.total_videos || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={6}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom variant="caption">
                        Watch Time (30d)
                      </Typography>
                      <Typography variant="h5" fontWeight={600}>
                        {Math.round(comprehensive?.metrics?.watchTimeHours || 0)}h
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Panel>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ChannelOverview;
